import _ from 'lodash';
import React, {useCallback, useEffect, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Badge, Btn, Icon, Main, Spacer} from '../../ui-toolkit';
import {NotificationCard} from '../../ui-toolkit/indexExportV2';
import {AdvancedFilters} from '../../ui-toolkit/page/advanced-filter/AdvancedFilter';

import {DataFilter} from '../../ducks/data-filter';
import {useEntityList} from '../../ducks/entity-list-v2';
import {useSpecificationSubscription} from '../../ducks/entitylist';
import {getPathDetails} from '../../ducks/formattersettingshelpers';
import {usePreferences} from '../../ducks/PreferencesContext';
import {GridItem} from '../../ui-toolkit/page/grid/GridItems.tsx';
import {useQuickFilters, withQuickFilters} from '../formatters/cell/EntityTable/quick-filters/QuickFiltersContext';
import {useFormatterById, useIndexedFormatters, useListFormatterByType} from '../FormattersContext';
import {buildFilterSpecification} from '../list-page-shared/buildFilterSpecification';
import {EntityContainerTab, EntityListGrid, EntityListHeader, EntityTableHeader} from './EntityListGrid';
import {ListComponent} from './ListComponent';

function EntitiesList({
    entityType,
    entityClick,
    markedRows,
    extraFilter,
    entityPostprocess,
    t,
    preferredFormatterId,
    pageSize,
    buttons,
}) {
    const {preferences, setPreference} = usePreferences();

    const showFilterPanel = _.get(preferences, 'showfilterSidebar', false);
    const currentSort = _.get(preferences, 'sortcol', null);
    const filters = _.get(preferences, 'filters', []);

    const indexedFormatters = useIndexedFormatters();
    const preferredFormatter = useFormatterById(preferredFormatterId);
    const defaultListFormatter = useListFormatterByType(entityType.dataTypeId);

    let extraAndGlobalFilter = extraFilter;

    // use preferred formatter, if specified, otherwise fallback to default one
    const tableFormatter = _.defaultTo(preferredFormatter, defaultListFormatter);

    let List = tableFormatter;
    let projectionStr = tableFormatter
        .projectionFunction(tableFormatter.settings, entityType, indexedFormatters)
        ?.join(', ');

    let filterFormatters = tableFormatter.settings.filters;

    // remove filters on attributes that no longer exist
    const validFilterFormatters = _.filter(
        filterFormatters,
        (config) => getPathDetails(entityType, config.attributePath).validPath,
    );

    const validFilterFormatterIds = new Set(_.map(validFilterFormatters, 'id'));

    const validFilters = _.filter(filters, ({id}) => validFilterFormatterIds.has(id));

    // if the formatter has a default filter, add it to the filters
    const componentExtraFilter = tableFormatter.extraFilterFunction(tableFormatter.settings, preferences);
    if (!_.isEmpty(componentExtraFilter)) {
        if (extraFilter) {
            extraAndGlobalFilter = extraFilter + '+' + componentExtraFilter;
        } else {
            extraAndGlobalFilter = componentExtraFilter;
        }
    }

    // build a filter query based on the currently active filters
    let query = buildFilterSpecification(entityType, filterFormatters, validFilters);
    if (extraAndGlobalFilter) {
        if (query) query += '+' + extraAndGlobalFilter;
        else query = extraAndGlobalFilter;
    }

    const quickFilters = useQuickFilters();
    if (quickFilters.any()) {
        query = DataFilter.and([...quickFilters.values(), query]);
    }

    const {reloadNeeded, setReloaded} = useSpecificationSubscription(entityType.dataTypeId, query);

    const entityList = useEntityList(entityType.dataTypeId, {
        filter: query,
        project: projectionStr,
        sort: currentSort,
        pageSize,
    });

    // user updated the filters, delegate to entity list, which triggers a reload
    useEffect(() => {
        entityList.setFilter(query);
        setReloaded();
    }, [entityList.setFilter, query, setReloaded]);

    // user updated the sort, delegate to entity list, which triggers a reload
    useEffect(() => {
        entityList.setSort(currentSort);
        setReloaded();
    }, [entityList.setSort, currentSort, setReloaded]);

    const doSetSort = useCallback(
        (sort) => {
            setPreference('sortcol', sort);
        },
        [setPreference],
    );

    const applyFilter = useCallback(
        (formValues) => {
            setPreference('filters', _.get(formValues, 'filters'));
        },
        [setPreference],
    );

    const doReload = useCallback(() => {
        entityList.reload();
        setReloaded();
    }, [entityList, setReloaded]);

    const toggleFilterPanel = useCallback(() => {
        setPreference('showfilterSidebar', !showFilterPanel);
    }, [setPreference, showFilterPanel]);

    const renderedButtons = buttons == null ? null : buttons({entityList});

    // the filterRef refers to the component that controls the filter form, so that other components in the entities list
    // can also add filters to the filter form. Refs are used here because we don't want everything to be wrapped inside the
    // FilterPanel Formik instance
    const filterRef = useRef();

    const filterOnField = useCallback(
        (fieldName, value) => {
            filterRef.current.filterOnField(fieldName, value);
            setPreference('showfilterSidebar', true);
        },
        [setPreference],
    );

    return (
        <>
            <Main>
                <EntityListGrid>
                    <EntityTableHeader>
                        {/* tabs component, depending on Formatter */}
                        <EntityContainerTab>
                            {!!List.TabsFormatter && <List.TabsFormatter formatter={tableFormatter} />}
                        </EntityContainerTab>

                        <Spacer.Align space="xsm" wrap>
                            {/* bulk action buttons, export to Excel, passed from parent */}
                            {renderedButtons}
                            {/* button to show the advanced filters sidebar only shown if there are filter formatters*/}
                            {!_.isEmpty(validFilterFormatters) && (
                                <EntityListHeader.FilterBtn
                                    color="primary"
                                    onClick={toggleFilterPanel}
                                    type="button"
                                    ghost={validFilters.length === 0}>
                                    <Icon type="sm" name="FilterAlt" />
                                    {t('filterPanel.advancedFilters')}
                                    {validFilters.length !== 0 && (
                                        <Badge color="danger">
                                            <span>{validFilters.length}</span>
                                        </Badge>
                                    )}
                                </EntityListHeader.FilterBtn>
                            )}
                            {/* refresh button */}
                            {doReload && (
                                <Btn color="primary" onClick={doReload} type="button" ghost={!reloadNeeded}>
                                    <Icon type="sm" name="Refresh" />
                                    {t('filterPanel.refresh')}
                                </Btn>
                            )}
                            {/* list component specific buttons, depends on Formatter */}
                            {!!List.PreferencesFormatter && (
                                <List.PreferencesFormatter formatter={tableFormatter} entityType={entityType} t={t} />
                            )}
                        </Spacer.Align>
                    </EntityTableHeader>
                    {!entityList.error && (
                        <GridItem area="main">
                            <ListComponent
                                tableFormatter={tableFormatter}
                                entityType={entityType}
                                entitiesState={entityList}
                                entityClick={entityClick}
                                entityPostprocess={entityPostprocess}
                                currentSort={currentSort}
                                doSetSort={doSetSort}
                                doLoadMore={entityList.loadMore}
                                markedRows={markedRows}
                                filterFormatters={filterFormatters}
                                filterOnField={filterOnField}
                                List={List}
                                t={t}
                            />
                        </GridItem>
                    )}

                    {entityList.error && (
                        <ErrorNotification color="danger">
                            <Spacer.Align space="xsm">
                                <Icon name="warning" />
                                {t('errors.errorLoadingEntities')}
                            </Spacer.Align>
                        </ErrorNotification>
                    )}
                </EntityListGrid>
            </Main>
            <AdvancedFilters
                filters={validFilters}
                applyFilter={applyFilter}
                validFilterFormatters={validFilterFormatters}
                filterRef={filterRef}
                showFilterPanel={showFilterPanel}
                toggleFilterPanel={toggleFilterPanel}
                entityType={entityType}
                t={t}
            />
        </>
    );
}

export default withTranslation()(withQuickFilters(EntitiesList));

const ErrorNotification = styled(NotificationCard)`
    height: min-content;
`;
